import React, { useState } from 'react';
import { UserPlus, Mail, ShieldCheck, CalendarCheck, ChevronDown, AlertTriangle, CreditCard, Info, ExternalLink } from 'lucide-react';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

/**
 * Tutorial component explaining the tennis booking flow.
 * Use on Tennis page and login pages to help new users.
 */
export const TennisBookingTutorial = ({ defaultOpen = false }) => {
  useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  const steps = [
    {
      number: '1',
      icon: UserPlus,
      title: t('pg.TennisBookingTutorial.c76310'),
      description:
        t('pg.TennisBookingTutorial.b9bc09'),
      color: 'bg-blue-500',
    },
    {
      number: '2',
      icon: CreditCard,
      title: t('pg.TennisBookingTutorial.1b73bb'),
      description:
        t('pg.TennisBookingTutorial.4a0f74'),
      action: {
        memberLabel: t('pg.TennisBookingTutorial.b3b45d'),
        memberDescription: t('pg.TennisBookingTutorial.aeb54f'),
        nonMemberLabel: t('pg.TennisBookingTutorial.f778ea'),
        nonMemberDescription: t('pg.TennisBookingTutorial.515650'),
        nonMemberLink: 'https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true',
        nonMemberButton: t('pg.TennisBookingTutorial.616bd0'),
      },
      color: 'bg-amber-500',
    },
    {
      number: '3',
      icon: ShieldCheck,
      title: t('pg.TennisBookingTutorial.a6fca3'),
      description:
        t('pg.TennisBookingTutorial.b100cf'),
      color: 'bg-green-500',
    },
    {
      number: '4',
      icon: Mail,
      title: t('pg.TennisBookingTutorial.2f8c87'),
      description:
        t('pg.TennisBookingTutorial.bfd5e2'),
      color: 'bg-purple-500',
    },
    {
      number: '5',
      icon: CalendarCheck,
      title: t('pg.TennisBookingTutorial.c9a358'),
      description:
        t('pg.TennisBookingTutorial.79723c'),
      color: 'bg-emerald-500',
    },
  ];

  return (
    <div
      data-testid="tennis-booking-tutorial"
      className="bg-gradient-to-br from-green-500/10 via-green-400/5 to-transparent border border-green-500/30 rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="tutorial-toggle"
        className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-green-500/5 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Info className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
              {t('pg.TennisBookingTutorial.3fcab2')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('pg.TennisBookingTutorial.65a9ad')}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-slate-500 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-6 pb-6 animate-fade-in-up">
          <div className="space-y-4">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  data-testid={`tutorial-step-${step.number}`}
                  className="flex gap-4 bg-white dark:bg-slate-800/80 rounded-xl p-4 border border-slate-200 dark:border-slate-700 hover:border-green-500/30 transition-colors"
                >
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-10 h-10 ${step.color} rounded-full flex items-center justify-center text-white font-bold shadow-md`}>
                      {step.number}
                    </div>
                    {idx < steps.length - 1 && (
                      <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 mt-2" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-green-500" aria-hidden="true" />
                      <h4 className="font-bold text-slate-900 dark:text-white">{step.title}</h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {step.description}
                    </p>
                    {step.action && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                          <p className="text-xs font-bold text-green-600 dark:text-green-400 mb-1">
                            {step.action.memberLabel}
                          </p>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            {step.action.memberDescription}
                          </p>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                          <p className="text-xs font-bold text-amber-600 dark:text-amber-400 mb-1">
                            {step.action.nonMemberLabel}
                          </p>
                          <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                            {step.action.nonMemberDescription}
                          </p>
                          <a
                            href={step.action.nonMemberLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hoopit-join-link"
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-300 hover:underline"
                          >
                            {step.action.nonMemberButton}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Important notice */}
          <div className="mt-5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white mb-1">{t('pg.TennisBookingTutorial.2864f9')}</p>
              <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1 list-disc list-inside">
                <li>{t('pg.TennisBookingTutorial.842a18')} <strong>{t('pg.TennisBookingTutorial.34bfd4')}</strong> {t('pg.TennisBookingTutorial.d07a21')}</li>
                <li>{t('pg.TennisBookingTutorial.f8f009')} <strong>{t('pg.TennisBookingTutorial.3eb462')}</strong></li>
                <li>{t('pg.TennisBookingTutorial.1b6b03')} <strong>{t('pg.TennisBookingTutorial.47bd18')}</strong></li>
                <li>{t('pg.TennisBookingTutorial.382f71')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TennisBookingTutorial;
